/**
 * Module-Aware Global Navigation
 * - Shows module dropdown selector in header
 * - Filters navigation based on current module
 * - Includes "Modules" link to return to launcher
 * - Shows module-specific tools dropdown
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Home, Building2, GitBranch, Wrench, PoundSterling, LayoutDashboard, 
  Users, Globe, Bell, Check, Zap, ChevronDown, Grid3X3, Settings,
  FileText, HandCoins, FolderOpen, Briefcase, Flag, MessageSquare,
  ShieldCheck, ClipboardCheck, Receipt, Calendar, Wallet, RefreshCw,
  BarChart3, Building, HardHat, Contact, User, LogOut, ClipboardList,
  AlertTriangle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { GoodacresNavLogo } from '@/components/GoodacresLogo';
import { API } from '@/App';
import { 
  MODULES, 
  MODULE_INFO, 
  MODULE_NAV, 
  MODULE_TOOLS,
  ADMIN_ITEMS,
  getModuleFromPath,
  canAccessModule,
  getAccessibleModules,
} from '@/config/modules';

// Icon mapping
const ICONS = {
  Home, Building2, GitBranch, Wrench, PoundSterling, LayoutDashboard,
  Users, Globe, Zap, FileText, HandCoins, FolderOpen, Briefcase, Flag,
  MessageSquare, ShieldCheck, ClipboardCheck, Receipt, Calendar, Wallet,
  RefreshCw, BarChart3, Building, HardHat, Contact, User, ClipboardList,
  AlertTriangle,
};

// Module colors for the dropdown
const MODULE_COLORS = {
  [MODULES.SALES]: 'text-emerald-400',
  [MODULES.PROGRESSION]: 'text-blue-400',
  [MODULES.LETTINGS]: 'text-violet-400',
  [MODULES.MAINTENANCE]: 'text-amber-400',
  [MODULES.ACCOUNTS]: 'text-slate-400',
};

export default function GlobalNav() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get user from localStorage
  const userStr = localStorage.getItem('user');
  const user = useMemo(() => userStr ? JSON.parse(userStr) : null, [userStr]);
  const token = localStorage.getItem('token');
  
  // Derive current module from path
  const currentModule = useMemo(() => getModuleFromPath(location.pathname), [location.pathname]);
  const moduleInfo = currentModule ? MODULE_INFO[currentModule] : null;
  
  // Get accessible modules for user
  const accessibleModules = useMemo(() => user ? getAccessibleModules(user) : [], [user]);
  
  // Check if on launcher
  const isOnLauncher = location.pathname === '/home' || location.pathname === '/dashboard';
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Module nav badge counters (e.g., maintenance open issues)
  const [navBadges, setNavBadges] = useState({});
  
  // Staff roles that can see notifications
  const staffRoles = ['admin', 'director', 'lettings_manager', 'accounts', 'sales_agent'];
  const canSeeNotifications = user && staffRoles.includes(user.role);
  
  useEffect(() => {
    if (canSeeNotifications && token) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [canSeeNotifications, token]);
  
  // Fetch badge counters when in the Maintenance module
  useEffect(() => {
    if (currentModule === MODULES.MAINTENANCE && canSeeNotifications && token) {
      fetchMaintenanceBadges();
      const interval = setInterval(fetchMaintenanceBadges, 60000);
      return () => clearInterval(interval);
    }
  }, [currentModule, canSeeNotifications, token]);
  
  const fetchMaintenanceBadges = async () => {
    try {
      const response = await axios.get(`${API}/maintenance/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNavBadges(prev => ({
        ...prev,
        open_issues: response.data?.open_issues || 0,
      }));
    } catch (error) {
      // Silent - badge is non-critical
    }
  };
  
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/staff-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };
  
  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/staff-notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const markAllAsRead = async () => {
    try {
      await axios.patch(`${API}/staff-notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };
  
  if (!user) return null;
  
  // Get navigation items for current module
  const moduleNavItems = currentModule ? (MODULE_NAV[currentModule] || []) : [];
  const moduleToolItems = currentModule ? (MODULE_TOOLS[currentModule] || []) : [];
  
  // Filter tools based on user role
  const filteredTools = moduleToolItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(user.role);
  });
  
  // Filter admin items based on role
  const filteredAdminItems = ADMIN_ITEMS.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(user.role);
  });
  
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('goodacres_last_module');
    navigate('/login');
  };
  
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  const getIcon = (iconName) => {
    const Icon = ICONS[iconName];
    return Icon ? <Icon className="h-4 w-4" /> : null;
  };

  return (
    <nav className="bg-[#1c1c1c] text-white sticky top-0 z-50" data-testid="global-nav">
      <div className="max-w-full mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Left: Logo + Module Selector + Navigation */}
          <div className="flex items-center gap-4">
            {/* Logo - goes to launcher */}
            <div 
              className="cursor-pointer" 
              onClick={() => navigate('/home')}
              data-testid="nav-logo"
            >
              <GoodacresNavLogo />
            </div>
            
            <div className="h-6 w-px bg-slate-600" />
            
            {/* Module Selector Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-slate-700 gap-2"
                  data-testid="module-selector"
                >
                  <Grid3X3 className="h-4 w-4" />
                  {moduleInfo ? (
                    <span className={MODULE_COLORS[currentModule]}>
                      {moduleInfo.title}
                    </span>
                  ) : (
                    <span className="text-slate-300">Modules</span>
                  )}
                  <ChevronDown className="h-3 w-3 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500">
                  Switch Module
                </DropdownMenuLabel>
                {accessibleModules.map(moduleId => {
                  const info = MODULE_INFO[moduleId];
                  const isCurrentModule = moduleId === currentModule;
                  return (
                    <DropdownMenuItem
                      key={moduleId}
                      onClick={() => navigate(info.defaultPath)}
                      className={isCurrentModule ? 'bg-slate-100' : ''}
                    >
                      <span className={`mr-2 ${MODULE_COLORS[moduleId]}`}>
                        {getIcon(info.icon)}
                      </span>
                      {info.title}
                      {isCurrentModule && (
                        <Check className="ml-auto h-4 w-4 text-emerald-500" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/home')}>
                  <Grid3X3 className="h-4 w-4 mr-2" />
                  All Modules
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Module Navigation Items */}
            {!isOnLauncher && currentModule && (
              <>
                <div className="h-6 w-px bg-slate-700" />
                <div className="flex items-center gap-1">
                  {moduleNavItems.slice(0, 7).map((item, idx) => {
                    const badgeCount = item.badgeKey ? (navBadges[item.badgeKey] || 0) : 0;
                    return (
                      <Button
                        key={`${item.path}-${idx}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(item.path)}
                        className={`text-sm relative ${
                          isActive(item.path) 
                            ? 'bg-slate-700 text-white' 
                            : 'text-slate-400 hover:text-white hover:bg-slate-700'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        {getIcon(item.icon)}
                        <span className="ml-1.5 hidden lg:inline">{item.label}</span>
                        {badgeCount > 0 && (
                          <span
                            className="ml-1.5 inline-flex items-center justify-center bg-red-500 text-white text-[10px] font-semibold rounded-full h-4 min-w-[16px] px-1"
                            data-testid={`nav-badge-${item.badgeKey}`}
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>
              </>
            )}
            
            {/* Tools Dropdown (when in a module) */}
            {!isOnLauncher && currentModule && filteredTools.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-slate-400 hover:text-white hover:bg-slate-700"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="ml-1.5 hidden lg:inline">Tools</span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {filteredTools.map((item, idx) => (
                    <DropdownMenuItem key={idx} onClick={() => navigate(item.path)}>
                      {getIcon(item.icon)}
                      <span className="ml-2">{item.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          
          {/* Right: Notifications + User Menu */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            {canSeeNotifications && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="relative p-2 text-slate-300 hover:text-white hover:bg-slate-700"
                    data-testid="notifications-bell"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
                  <div className="px-3 py-2 border-b flex items-center justify-between">
                    <span className="font-semibold text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={markAllAsRead}>
                        <Check className="h-3 w-3 mr-1" />
                        Mark all read
                      </Button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <DropdownMenuItem 
                        key={notif.notification_id}
                        className={`flex-col items-start p-3 cursor-pointer ${!notif.read ? 'bg-blue-50' : ''}`}
                        onClick={() => !notif.read && markAsRead(notif.notification_id)}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {!notif.read && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                          <span className="font-medium text-sm">{notif.title}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 ml-4">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-1 ml-4">
                          {new Date(notif.created_at).toLocaleDateString('en-GB', { 
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                          })}
                        </p>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            <span className="text-sm text-slate-400 hidden md:block">
              {user.name || user.email}
            </span>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-0 h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-700 text-white text-xs">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-slate-500">{user.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/home')}>
                  <Grid3X3 className="h-4 w-4 mr-2" />
                  Modules
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-dashboard')}>
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  My Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-profile')}>
                  <User className="h-4 w-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/contacts')}>
                  <Contact className="h-4 w-4 mr-2" />
                  Contacts
                </DropdownMenuItem>
                
                {/* Admin Items */}
                {filteredAdminItems.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-slate-500">Admin</DropdownMenuLabel>
                    {filteredAdminItems.map((item, idx) => (
                      <DropdownMenuItem key={idx} onClick={() => navigate(item.path)}>
                        {getIcon(item.icon)}
                        <span className="ml-2">{item.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
}
